import {IRuleSystem} from '../../models/svg-rule/rule-system.model';
import {Color} from '../../enums/color';
import {TcddTrainConditions} from '../../svg-condition/tcdd-train-conditions';
import {TcddTrainTabUtil, ValueIndex} from '../../utils/tcdd-train-tab-util';
import {CacheKeys} from '../../enums/cache-keys.enum';
import {TcddMainSystem} from '../../enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../../enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../../enums/tcdd-sub-system-type.enum';
import {MainSystemKeys} from '../../constants/tcdd-system-keys/main-system-keys';
import {TractionSystemKeys} from '../../constants/tcdd-system-keys/traction-system-keys';
import {CommunicationSystemKeys} from '../../constants/tcdd-system-keys/communication-system-keys';
import {OtherSystemKeys} from '../../constants/tcdd-system-keys/other-system-keys';
import {OTHER_SYSTEM} from '../../constants/tcdd-system-contants/other-system.constant';
import {TCDD_UNIT} from '../../constants/tcdd-unit-constant';
import {createColorRule, createIconRule, createTextRule} from '../../utils/tcdd-rule-util';

interface IndexedKey {
  key: string;
  value: {groupIndex: number, infoIndex: number} | null;
}

export class MainSystemRules implements IRuleSystem {
  private static readonly FIRE_ALARM: string = 'train/fire-alarm.svg';
  private static readonly WARNING: string = 'train/warning.svg';

  private static getLastWagonIndex(data: any): number | null {
    const wagonIndexData = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.wagonIndex];
    return wagonIndexData ? Object.keys(wagonIndexData).length - 1 : null;
  }

  private static getKeyByValue(obj: Record<string, number>, value: number): string | undefined {
    return Object.keys(obj).find(key => obj[key] === value);
  }

  private static getSelectedData(subSystem: TcddSubSystem, subSystemType: TcddSubSystemType, wagonIndex: number, data: any): any | null {
    if (!data) return null;

    if (subSystemType === TcddSubSystemType.MAIN) {
      return data[subSystem] ?? null;
    }

    if (subSystemType === TcddSubSystemType.SUB) {
      return data[subSystem]?.[wagonIndex] ?? null;
    }

    return null;
  }

  private static isUdsUibErrorCase(subSystem: TcddSubSystem, subSystemType: TcddSubSystemType, wagonIndex: number, data: any): boolean {
    if (wagonIndex == null) return false;

    const mainSystem = data?.[TcddSubSystem.MAIN_SYSTEM];
    return (
      subSystemType === TcddSubSystemType.SUB &&
      subSystem === TcddSubSystem.COMMUNICATION_SYSTEM &&
      wagonIndex === mainSystem?.[MainSystemKeys.udsUibWagonIndex] &&
      Boolean(mainSystem?.[MainSystemKeys.udsUibError])
    );
  }

  private static buildValueIndexesForCoupling(subSystem: TcddSubSystem): any {
    const result: IndexedKey[] = [];

    switch (subSystem) {
      case TcddSubSystem.TRACTION_SYSTEM:
        [TractionSystemKeys.isCouplingProcessCompleted, TractionSystemKeys.isDecouplingDone].forEach((infoKey) => {
          result.push({
            key: infoKey,
            value: TcddTrainTabUtil.getValueIndex(
              TcddMainSystem.DIAGNOSTIC_SYSTEM,
              TcddSubSystem.TRACTION_SYSTEM,
              TcddSubSystemType.MAIN,
              infoKey
            )
          });
        });
        break;

      default:
        break;
    }

    return result;
  }

  private static buildValueIndexesForCab(subSystem: TcddSubSystem): any {
    const result: IndexedKey[] = [];

    switch (subSystem) {
      case TcddSubSystem.OTHER_SYSTEM:
        [OtherSystemKeys.passengersAlarm, OtherSystemKeys.deadManControl, OtherSystemKeys.deadManByPass].forEach((infoKey) => {
          result.push({
            key: infoKey,
            value: TcddTrainTabUtil.getValueIndex(
              TcddMainSystem.DIAGNOSTIC_SYSTEM,
              TcddSubSystem.OTHER_SYSTEM,
              TcddSubSystemType.SUB,
              infoKey
            )
          });
        });
        break;

      case TcddSubSystem.COMMUNICATION_SYSTEM:
        [CommunicationSystemKeys.hmi].forEach((infoKey) => {
          result.push({
            key: infoKey,
            value: TcddTrainTabUtil.getValueIndex(
              TcddMainSystem.DIAGNOSTIC_SYSTEM,
              TcddSubSystem.COMMUNICATION_SYSTEM,
              TcddSubSystemType.SUB,
              infoKey
            )
          });
        });
        break;

      default:
        break;
    }

    return result;
  }

  private static isValueIndexInList(list: IndexedKey[], groupIndex: number, infoIndex: number): boolean {
    return list.some(item =>
      item.value != null &&
      item.value.groupIndex === groupIndex &&
      item.value.infoIndex === infoIndex
    );
  }

  private static isCabWagonSkipCase(data: any, wagonIndex: number): boolean {
    if (!data || wagonIndex == null) return false;

    const cabActive = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.cabActive] ?? '0';
    const lastWagonIndex = MainSystemRules.getLastWagonIndex(data);

    // 0: No active cab, 2: SKA1 active cab, 1: SKA2 active cab, 3: Error
    switch (cabActive) {
      case '1':
        return wagonIndex !== lastWagonIndex;
      case '2':
        return wagonIndex !== 0;
      default:
        return true;
    }
  }

  public getRules(): any {
    return [
      createColorRule(TCDD_UNIT.COUPLER, (wagonIndex: number, data: any, setColor: (color: Color) => void) => {
        if (wagonIndex == null || !data || !data[TcddSubSystem.MAIN_SYSTEM]) return false;

        const lastWagonIndex = MainSystemRules.getLastWagonIndex(data);
        if (wagonIndex !== 0 && wagonIndex !== lastWagonIndex) return false;

        const index = wagonIndex === 0 ? 0 : 1;

        // Variables(ConnectionIndicator) that track the direction of the trains or whether the coupler is open (0 = null, 1 = coupler, 2 = direction).
        const couplerData = data[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.connectionIndicator]?.[index];
        if (couplerData == null) return false;

        if (couplerData === 0 || couplerData === 2) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.DIRECTION, (wagonIndex: number, data: any, setColor: (color: Color) => void) => {
        if (wagonIndex == null || !data || !data[TcddSubSystem.MAIN_SYSTEM]) return false;

        const lastWagonIndex = MainSystemRules.getLastWagonIndex(data);
        if (wagonIndex !== 0 && wagonIndex !== lastWagonIndex) return false;

        const index = wagonIndex === 0 ? 0 : 1;

        // Variables(ConnectionIndicator) that track the direction of the trains or whether the coupler is open (0 = null, 1 = coupler, 2 = direction).
        const directionData = data[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.connectionIndicator]?.[index];
        if (directionData == null) return false;

        if (directionData === 0 || directionData === 1) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.ACTIVE_CAB_CONTAINER, (wagonIndex: number, data: any, setColor: (color: Color) => void) => {
        if (wagonIndex == null || !data || !data[TcddSubSystem.MAIN_SYSTEM]) return false;

        // 0: No active cab, 2: SKA1 active cab, 1: SKA2 active cab, 3: Error
        const cabActiveData = data[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.cabActive];
        if (cabActiveData == null) return false;

        const lastWagonIndex = MainSystemRules.getLastWagonIndex(data);

        if (wagonIndex === 0 && (cabActiveData === '0' || cabActiveData === '1' || cabActiveData === '3')) {
          setColor(Color.Transparent);
          return true;
        }

        if (wagonIndex === lastWagonIndex && (cabActiveData === '0' || cabActiveData === '2' || cabActiveData === '3')) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createIconRule(
        TCDD_UNIT.TITLE_ICON,
        (subSystem: TcddSubSystem, wagonIndex: number, data: any, setColor, setIcon, setIconType) => {
          const subSystemType = wagonIndex == null ? TcddSubSystemType.MAIN : TcddSubSystemType.SUB;
          const couplingIndexes  = MainSystemRules.buildValueIndexesForCoupling(subSystem);
          const cabIndexes  = MainSystemRules.buildValueIndexesForCab(subSystem);
          const isCabWagonSkipCase = MainSystemRules.isCabWagonSkipCase(data, wagonIndex);

          // Retrieve conditions for the given system and group type.
          const systemConditions = TcddTrainConditions.getConditions()?.[subSystem]?.[subSystemType] ?? [];
          const selectedData = MainSystemRules.getSelectedData(subSystem, subSystemType, wagonIndex, data);

          // UDS-UIB Error
          if (MainSystemRules.isUdsUibErrorCase(subSystem, subSystemType, wagonIndex, data)) {
            setIcon(MainSystemRules.WARNING);
            setIconType(CacheKeys.TRAIN_WARNING_ICON);
            setColor(Color.Transparent);
            return true;
          }

          if (!selectedData) return false;

          if (subSystemType === TcddSubSystemType.SUB && subSystem === TcddSubSystem.OTHER_SYSTEM) {
            const fireSystemAlarm: ValueIndex = TcddTrainTabUtil.getValueIndex(TcddMainSystem.DIAGNOSTIC_SYSTEM, TcddSubSystem.OTHER_SYSTEM, TcddSubSystemType.SUB, OtherSystemKeys.fireSystemAlarm); // === 0 will add fire icon
            if (fireSystemAlarm != null && Number(selectedData[fireSystemAlarm.groupIndex]?.[fireSystemAlarm.infoIndex]) === OTHER_SYSTEM.STATUS.FIRE) {
              setIcon(MainSystemRules.FIRE_ALARM);
              setIconType(CacheKeys.TRAIN_FIRE_ALARM_ICON);
              setColor(Color.Transparent);
              return true;
            }
          }

          // Iterate over each condition group to determine if any condition is met.
          return systemConditions.some((conditionGroup, groupIndex: number) => {
            const groupData = selectedData[groupIndex] ?? [];

            return conditionGroup.conditions.some((condition, conditionIndex: number) => {
              const value = groupData[conditionIndex];

              if (value === '-') {
                return false;
              }

              if (
                subSystemType === TcddSubSystemType.MAIN &&
                !data[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.couplingStatus] &&
                MainSystemRules.isValueIndexInList(couplingIndexes , groupIndex, conditionIndex)
              ) {
                return false;
              }

              if (
                subSystemType === TcddSubSystemType.SUB &&
                isCabWagonSkipCase &&
                MainSystemRules.isValueIndexInList(cabIndexes , groupIndex, conditionIndex)
              ) {
                return false;
              }

              if (condition(value)) {
                setIcon(MainSystemRules.WARNING);
                setIconType(CacheKeys.TRAIN_WARNING_ICON);
                setColor(Color.Transparent);
                return true;
              }
            });
          });
        },
        {
          iconSize: 24,
          offsetX: 5,
          offsetY: 0,
          position: 'right'
        }
      ),
      createTextRule(
        TCDD_UNIT.TEXT_TITLE,
        (wagonIndex: number, data: any, setColor, setText) => {
          if (wagonIndex == null || !data || !data[TcddSubSystem.MAIN_SYSTEM]) return false;

          // 2: lead consist, 1: follower consist, 0 or 3: error
          const consistData = data[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.leadFollowConsist];
          const wagonType = MainSystemRules.getKeyByValue(data[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.wagonIndex], wagonIndex);
          if (consistData == null || wagonType == null) return false;

          if (consistData === '2') {
            setText('primary train' + ' - ' + wagonType);
            setColor(Color.White);
            return true;
          }

          if (consistData === '1') {
            setText('secondary train' + ' - ' + wagonType);
            setColor(Color.White);
            return true;
          }

          setText(wagonType);
          setColor(Color.White);
          return true;
        },
        {
          fontSize: '18',
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: '300',
          offsetX: 0,
          offsetY: 0,
          position: 'top',
          textAnchor: 'middle'
        }
      )
    ];
  }
}
